import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  FileText,
  Upload,
  BarChart3,
  Trophy,
  Calendar,
  Building,
  Users,
  Wrench,
  Settings,
  Plus,
  Search,
} from "lucide-react";

interface CommandAction {
  id: string;
  title: string;
  description?: string;
  icon: React.ElementType;
  action: () => void;
  group: string;
  keywords?: string[];
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { userRole } = useAuth();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const commands: CommandAction[] = [
    // Navigation
    {
      id: "dashboard",
      title: "Dashboard",
      description: "Vue d'ensemble de l'activité",
      icon: LayoutDashboard,
      action: () => navigate("/"),
      group: "Navigation",
      keywords: ["accueil", "tableau", "bord"]
    },
    {
      id: "appels-offres",
      title: "Appels d'Offres",
      description: "Gérer tous les appels d'offres",
      icon: FileText,
      action: () => navigate("/appels-offres"),
      group: "Navigation",
      keywords: ["ao", "tender", "consultation"]
    },
    {
      id: "ranking",
      title: "Ranking Sociétés",
      description: "Classement des entreprises",
      icon: Trophy,
      action: () => navigate("/ranking-societes"),
      group: "Navigation",
      keywords: ["classement", "entreprises", "societes"]
    },
    
    // Actions rapides - only for admins
    ...(userRole === 'admin' ? [{
      id: "nouvel-ao",
      title: "Nouvel Appel d'Offres",
      description: "Créer un nouveau dossier",
      icon: Plus,
      action: () => navigate("/appels-offres/nouveau"),
      group: "Actions rapides",
      keywords: ["creer", "nouveau", "ajouter"]
    }] : []),
    {
      id: "import-cv",
      title: "Importer des CV",
      description: "Télécharger des candidatures",
      icon: Upload,
      action: () => navigate("/import-cv"),
      group: "Actions rapides",
      keywords: ["telecharger", "candidature", "document"]
    },
    
    // Administration
    {
      id: "analyses",
      title: "Analyses IA",
      description: "Voir tous les résultats d'analyse",
      icon: BarChart3,
      action: () => navigate("/analyses"),
      group: "Administration",
      keywords: ["scoring", "evaluation", "resultats", "ia"]
    },
    {
      id: "planning",
      title: "Planning AO",
      description: "Vue chronologique des projets",
      icon: Calendar,
      action: () => navigate("/planning-ao"),
      group: "Administration",
      keywords: ["gantt", "chronologie", "projet"]
    },
    {
      id: "planning-gantt",
      title: "Planning Gantt",
      description: "Vue Gantt des appels d'offres",
      icon: BarChart3,
      action: () => navigate("/planning-ao?view=gantt"),
      group: "Administration",
      keywords: ["gantt", "chronologie", "diagramme"]
    },
    {
      id: "societes",
      title: "Sociétés",
      description: "Gérer les entreprises",
      icon: Building,
      action: () => navigate("/societes"),
      group: "Administration",
      keywords: ["entreprise", "company"]
    },
    {
      id: "collaborateurs",
      title: "Collaborateurs",
      description: "Gérer l'équipe",
      icon: Users,
      action: () => navigate("/collaborateurs"),
      group: "Administration",
      keywords: ["equipe", "utilisateurs", "team"]
    },
    {
      id: "services",
      title: "Services",
      description: "Configuration des services",
      icon: Wrench,
      action: () => navigate("/services"),
      group: "Administration",
      keywords: ["departement", "config"]
    },
    {
      id: "architecture",
      title: "Architecture",
      description: "Vue d'ensemble de l'architecture",
      icon: BarChart3,
      action: () => navigate("/architecture"),
      group: "Administration",
      keywords: ["architecture", "schema", "structure"]
    },
    {
      id: "parametres",
      title: "Paramètres",
      description: "Configuration de l'application",
      icon: Settings,
      action: () => navigate("/parametres"),
      group: "Administration",
      keywords: ["config", "settings", "preferences"]
    },
  ];

  const handleAction = (action: () => void) => {
    action();
    setOpen(false);
  };

  const groupedCommands = commands.reduce((acc, command) => {
    if (!acc[command.group]) {
      acc[command.group] = [];
    }
    acc[command.group].push(command);
    return acc;
  }, {} as Record<string, CommandAction[]>);

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Rechercher une page ou action..." />
        <CommandList>
          <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
          {Object.entries(groupedCommands).map(([group, commands], index) => (
            <div key={group}>
              {index > 0 && <CommandSeparator />}
              <CommandGroup heading={group}>
                {commands.map((command) => (
                  <CommandItem
                    key={command.id}
                    onSelect={() => handleAction(command.action)}
                    className="flex items-center gap-2 px-2 py-3"
                  >
                    <command.icon className="h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{command.title}</span>
                      {command.description && (
                        <span className="text-xs text-muted-foreground">
                          {command.description}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}

export default CommandPalette;